import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import * as api from "../../services/api";
import BookingConfirmationDocument from "../../models/BookingConfirmationDocument";
import BookingFinance from "../../models/BookingFinance";
import BookingFinanceDocument from "../../models/BookingFinanceDocument";


export const useBookingsQuery = () => {
    return useQuery({
        queryKey: ["bms", "bookings"],
        queryFn: () => api.Bookings.getAllBooking(),
        staleTime: 15 * 60 * 1000, // 15 minutes
    });
}


export const useAvailabilityQuery = (options) => {
    return useQuery({
        queryKey: ["bms", "availability", options],
        queryFn: () => api.Bookings.getAvailability(options),
        enabled: !!options
    });
}

export const useBookingQuery = (bookingId, options={}) => {
    return useQuery({
        queryKey: ["bms", "bookings", Number(bookingId)],
        queryFn: () => api.Bookings.getBooking(bookingId),
        enabled: !!bookingId,
        ...options
    });
}

export const useBookingCreate = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({data}) => api.Bookings.createBooking(data),
        onSuccess: (result) => {
            queryClient.invalidateQueries(["bms", "bookings"]);
            queryClient.setQueryData(['bms', 'bookings'], (old) => [...(old || []), result]);
        }
    })

}

export const useBookingUpdate = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({id, data}) => api.Bookings.updateBooking(id, data),
        onSuccess: (result) => {
            queryClient.invalidateQueries(["bms", "bookings"]);
            queryClient.setQueryData(['bms', 'bookings', result.id], result);
        }
    })
}

export const useBookingDelete = ({bookingId}) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: () => api.Bookings.deleteBooking(bookingId),
        onSuccess: () => {
            queryClient.invalidateQueries(["bms", "bookings"]);
            queryClient.setQueryData(['bms', 'bookings'], (old) => old?.filter(b => b.id !== bookingId));
        }
    })
}

export const useBookingCancel = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({id}) => api.Bookings.cancelBooking(id),
        onSuccess: (result) => {
            queryClient.invalidateQueries(["bms", "bookings"]);
            queryClient.setQueryData(['bms', 'bookings', result.id], result);
            queryClient.setQueryData(['bms', 'bookings'], (old) => old?.filter(b => b.id !== result.id));
        }
    })

}

export const useBookingUpdatePeriod = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({id, data}) => api.Bookings.updateBookingPeriod(id, data),
        onSuccess: (result) => {
            queryClient.invalidateQueries(["bms", "bookings"]);
            queryClient.setQueryData(['bms', 'bookings', result.id], result);
        }
    })
}

export const useBookingItemUpdate = ({bookingId}) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({data, bookingItemId}) => {
            return api.Bookings.updateBookingItem(bookingItemId, data)
        },
        onSuccess: (data) => {
            queryClient.invalidateQueries(["bms", "bookings"]);
            queryClient.setQueryData(['bms', 'bookings', bookingId], (old) => {
                const booking = old.clone ? old.clone() : old;
                const bookingItemIndex = booking.bookingItems.findIndex(item => item.id === data.id);
                if (bookingItemIndex !== -1) {
                    booking.bookingItems[bookingItemIndex] = data;
                }
                return booking;
            });
        }
    })
}

export const useBookingItemCreate = ({bookingId}) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({data}) => {
            return api.Bookings.createBookingItem(bookingId, data)
        },
        onSuccess: (data) => {
            queryClient.invalidateQueries(["bms", "bookings"]);
            queryClient.setQueryData(['bms', 'bookings', bookingId], (old) => {
                const booking = old.clone ? old.clone() : old;
                booking.bookingItems.push(data);
                return booking;
            });
        }
    })
}

export const useBookingItemDelete = ({bookingId}) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (bookingItemId) => {
            return api.Bookings.deleteBookingItem(bookingItemId)
        },
        onSuccess: (data) => {
            queryClient.invalidateQueries(["bms", "bookings"]);
            queryClient.setQueryData(['bms', 'bookings', bookingId], (old) => {
                const booking = old.clone ? old.clone() : old;
                booking.bookingItems = booking.bookingItems.filter(item => item.id !== data?.id);
                return booking;
            });
        }
    })
}


export const useGuestCreate = ({bookingId}) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({data}) => api.Bookings.addGuest(bookingId, data),
        onSuccess: () => {
            queryClient.invalidateQueries(["bms", "bookings", bookingId]);
        }
    })
}

export const useGuestUpdate = ({bookingId}) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({guestId, data}) => api.Bookings.updateGuest(guestId, data),
        onSuccess: () => {
            queryClient.invalidateQueries(["bms", "bookings", bookingId]);
        }
    })
}


export const useGuestDelete = ({bookingId}) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({guestId}) => api.Bookings.deleteGuest(guestId),
        onSuccess: () => {
            queryClient.invalidateQueries(["bms", "bookings", bookingId]);
        }
    })
}


export const useCheckInCreate = ({bookingId}) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({data}) => api.Bookings.createCheckIn(bookingId, data),
        onSuccess: () => {
            queryClient.invalidateQueries(["bms", "bookings", bookingId]);
        }
    })
}


export const useCheckInCancel = ({bookingId}) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({checkInId}) => api.Bookings.cancelCheckIn(checkInId),
        onSuccess: () => {
            queryClient.invalidateQueries(["bms", "bookings", bookingId]);
        }
    })
}

export const useCheckInCreateAlloggiatiSync= ({bookingId}) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({checkInId}) => api.Bookings.checkInCreateAlloggiatiSync(checkInId),
        onSuccess: () => {
            queryClient.invalidateQueries(["bms", "bookings", bookingId]);
        }
    })
}

export const useCheckOutCreate = ({bookingId}) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({data}) => api.Bookings.createCheckOut(bookingId, data),
        onSuccess: () => {
            queryClient.invalidateQueries(["bms", "bookings", bookingId]);
        }
    })
}


export const usePaymentCreate = ({bookingId}) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({data}) => api.BookingFinances.addPayment(bookingId, data),
        onSuccess: (data) => {
            queryClient.invalidateQueries(["bms", "bookings"]);
            queryClient.invalidateQueries(["bms", "bookings", bookingId]);

            queryClient.setQueryData(['bms', 'bookings', bookingId], (old) => {
                const booking = old.clone ? old.clone() : old;
                booking.bookingFinance.addPayment(data);
                return booking;
            });
        }
    })
}


export const usePaymentDelete = ({bookingId}) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({paymentId}) => api.BookingFinances.deletePayment(bookingId, paymentId),
        onSuccess: (data) => {
            queryClient.invalidateQueries(["bms", "bookings"]);
            queryClient.invalidateQueries(["bms", "bookings", bookingId]);

            queryClient.setQueryData(['bms', 'bookings', bookingId], (old) => {
                const booking = old.clone ? old.clone() : old;
                booking.bookingFinance.removePayment(data.id);
                return booking;
            });
        }
    })
}


export const useSendOnlineCheckInEmail = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({bookingId}) => api.Bookings.sendOnlineCheckInEmail(bookingId),
        onSuccess: (result) => {
            queryClient.invalidateQueries(["bms", "bookings", result.id]);
        }
    })
}


export const useBookingConfirmationDocumentCreate = ({bookingId}) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: () => api.Bookings.createBookingConfirmationDocument(bookingId),
        onSuccess: (data) => {
            queryClient.invalidateQueries(["bms", "bookings", bookingId]);
            queryClient.setQueryData(['bms', 'bookings', bookingId], (old) => {
                const booking = old.clone ? old.clone() : old;
                booking.bookingConfirmationDocument = new BookingConfirmationDocument(data);
                return booking;
            });
        }
    });
}


export const useSendBookingConfirmationDocumentEmail = ({bookingId}) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: () => api.Bookings.sendBookingConfirmationDocumentEmail(bookingId),
        onSuccess: (data) => {
            queryClient.invalidateQueries(["bms", "bookings", bookingId]);
            queryClient.setQueryData(['bms', 'bookings', bookingId], (old) => {
                const booking = old.clone ? old.clone() : old;
                booking.bookingConfirmationDocument = new BookingConfirmationDocument(data);
                return booking;
            });
        }
    });
}

export const useBookingConfirmationDocumentPolling = (bookingId, enabled) => {
    const queryClient = useQueryClient();

    return useQuery({
        queryKey: ["bms", "bookings", bookingId, "bookingConfirmationDocument"],
        queryFn: () => api.Bookings.getBookingConfirmationDocument(bookingId),
        enabled: enabled,
        refetchInterval: (data) => {
            const bookingConfirmationDocument = data;
            if (bookingConfirmationDocument) {
                const pdfStatus = bookingConfirmationDocument.pdfStatus;
                const emailStatus = bookingConfirmationDocument.emailStatus;
                if (pdfStatus === 'pending' || emailStatus === 'pending') {
                    return 3000; // refetch every 3 seconds
                }
            }
            return false; // stop refetching
        },
        onSuccess: (data) => {
            queryClient.setQueryData(['bms', 'bookings', bookingId], old => {
                const booking = old.clone ? old.clone() : old;
                booking.bookingConfirmationDocument = new BookingConfirmationDocument(data);
                return booking;
            });
        }
    });
}

export const useBookingFinanceUpdate = ({bookingId}) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({data}) => api.BookingFinances.updateBookingFinance(bookingId, data),
        onSuccess: (result) => {
            queryClient.invalidateQueries(["bms", "bookings", bookingId]);
            queryClient.setQueryData(['bms', 'bookings', bookingId], old => {
                const booking = old.clone ? old.clone() : old;
                booking.bookingFinance = new BookingFinance(result);
                return booking;
            });
        }
    })
}

export const useBookingFinanceDocumentCreate = ({bookingId}) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: () => api.BookingFinances.createBookingFinanceDocument(bookingId),
        onSuccess: (data) => {
            queryClient.invalidateQueries(["bms", "bookings", bookingId]);
            queryClient.setQueryData(['bms', 'bookings', bookingId], (old) => {
                const booking = old.clone ? old.clone() : old;
                booking.bookingFinance.bookingFinanceDocument = new BookingFinanceDocument(data);
                return booking;
            });
        }
    });
}

export const useBookingFinanceDocumentPolling = (bookingId, enabled) => {
    const queryClient = useQueryClient();

    return useQuery({
        queryKey: ["bms", "bookings", bookingId, "bookingFinanceDocument"],
        queryFn: () => api.BookingFinances.getBookingFinanceDocument(bookingId),
        enabled: enabled,
        refetchInterval: (data) => {
            const bookingFinanceDocument = data;
            if (bookingFinanceDocument) {
                const pdfStatus = bookingFinanceDocument.pdfStatus;
                if (pdfStatus === 'pending') {
                    return 3000; // refetch every 3 seconds
                }
            }
            return false; // stop refetching
        },
        onSuccess: (data) => {
            queryClient.setQueryData(['bms', 'bookings', bookingId], old => {
                const booking = old.clone ? old.clone() : old;
                if (booking.bookingFinance) {
                    booking.bookingFinance.bookingFinanceDocument = new BookingFinanceDocument(data);
                }
                return booking;
            });
        }
    });
}